/**
 * What a click on a KPI value should DO.
 *
 * The interaction is chosen from the cell's provenance rather than from its
 * type, because what the user wants is always "show me the document behind this
 * number" and only the shape of the provenance decides whether that is one
 * document or a choice between several.
 *
 * - A stored value has its own citation: open it. One click.
 * - A computed value with exactly ONE stored input behind it is a pass-through
 *   — a single-property rollup, or a line filled from one component. Making the
 *   user pick from a list of one is a click that asks a question with only one
 *   answer, so it collapses to opening that input.
 * - A computed value with several inputs is a genuine choice: which of these
 *   numbers do you want to check? That is the only case that earns an
 *   intermediate step.
 * - Anything else — no citation, an unopenable file, nothing extracted — has no
 *   document to open, so the expansion is where it explains itself.
 */

import type { KpiCell, KpiCellInput, KpiSourceRef } from "../../types/kpi";
import { type KpiCitedNumber, kpiSourceKind } from "./kpiSourceUri";

export type KpiCellAction =
	/** Open this source in the viewer, straight away — `cited` is the number the
	 * source is being opened FOR, so a PDF can point at it. */
	| { kind: "open"; source: KpiSourceRef; cited: KpiCitedNumber }
	/** Show the in-grid breakdown: the formula and the inputs to choose from. */
	| { kind: "expand" };

function openable(source: KpiSourceRef | null | undefined): source is KpiSourceRef {
	return Boolean(source) && kpiSourceKind(source) !== "unopenable";
}

export function kpiCellAction(cell: KpiCell | undefined): KpiCellAction {
	if (!cell) return { kind: "expand" };

	// A stored value: its own citation is the answer.
	if (openable(cell.source)) {
		return {
			kind: "open",
			source: cell.source,
			cited: { value: cell.value, unit: cell.unit },
		};
	}

	const cited = (cell.inputs ?? []).filter((input) => openable(input.source));
	// Pass-through: one input behind this number, so there is nothing to choose.
	if (cited.length === 1) {
		return {
			kind: "open",
			source: cited[0].source as KpiSourceRef,
			cited: kpiInputCitedNumber(cited[0]),
		};
	}

	return { kind: "expand" };
}

/**
 * The number an input's citation is for: what extraction read, before any
 * override — an overridden figure is not the one printed in the document.
 */
export function kpiInputCitedNumber(input: KpiCellInput): KpiCitedNumber {
	return { value: input.extracted_value ?? input.value, unit: input.unit };
}

/**
 * Which stored row a cell's override should write to, if any.
 *
 * The same pass-through reasoning the click routing uses, applied to editing
 * rather than opening. A stored cell is its own target. A computed cell backed
 * by exactly ONE stored row IS that row — a single-property portfolio total, a
 * line filled from one component — and refusing the override there tells the
 * owner of a one-property portfolio that their own number cannot be corrected,
 * which is false and reads as the product not understanding its own data.
 *
 * Several rows behind a number means there is no single answer to "which value
 * did you mean?", and guessing one would silently edit an input the user never
 * chose. Those keep the refusal.
 */
export type KpiOverrideTarget =
	/** The cell's own row. */
	| { kind: "self"; valueId: string }
	/** One stored row standing behind a computed cell. */
	| { kind: "input"; valueId: string; input: KpiCellInput }
	/** Nothing single to write to. */
	| { kind: "none" };

export function kpiOverrideTarget(cell: KpiCell | undefined): KpiOverrideTarget {
	if (!cell) return { kind: "none" };
	if (cell.value_id) return { kind: "self", valueId: cell.value_id };

	const stored = (cell.inputs ?? []).filter((input) => Boolean(input.value_id));
	if (stored.length === 1) {
		return {
			kind: "input",
			valueId: stored[0].value_id as string,
			input: stored[0],
		};
	}
	return { kind: "none" };
}
